import { ServiceError } from 'common/exceptions'
import { logCallByBridgeRequest, logCallByBridgeResponse } from 'common/loggers/baseServiceLogger'
import { BrowserHttpService } from 'common/services/browserHttpService'
import { EnvVariableService } from 'common/services/envService'
import type { ErrorResponse } from 'store/error/types'

import { type IWbHttpData, interWbHttp } from '@interco/inter-webview-bridge'

import BridgeService from '../bridgeService'
import { EnvVariableKeys } from '../envService/types'
import { MonitoringService } from '../monitoringService'
import type { MultiplatformHttpRequestConfig, ServiceResponse } from './types'

const STATUS_CODE_ACCEPTED = [400, 404]
const BRIDGE_MESSAGES_ACCEPTED = [
  'Parece que você está sem internet ou sua conexão encontra-se instável.',
]

export class MultiplatformHttpService {
  static async callByBrowser<T>(
    config: MultiplatformHttpRequestConfig,
  ): Promise<ServiceResponse<T>> {
    const { method, endpoint, headers, data, params, urlPrefix } = config
    const response = await BrowserHttpService.request(endpoint.browser, {
      method,
      baseURL: urlPrefix,
      headers: headers.browser,
      params,
      body: data ? JSON.stringify(data) : undefined,
    })

    const headersResponse: Record<string, string> = {}

    Object.keys(response.headers).forEach((key) => {
      headersResponse[key] = response.headers.get(key) || ''
    })

    if (!response.ok) {
      let responseError
      try {
        responseError = await response.json()
      } catch (e: unknown) {
        console.log(e)
      }

      throw new ServiceError(
        STATUS_CODE_ACCEPTED.includes(response.status),
        responseError,
        'Something went wrong in the request, see the data object for more details.',
        response.status,
        headersResponse,
      )
    }

    return {
      headers: headersResponse,
      status: response.status,
      data: (await response.json()) as T,
    } as ServiceResponse<T>
  }

  static async callByBridge<T>(
    requestConfig: MultiplatformHttpRequestConfig,
  ): Promise<ServiceResponse<T>> {
    logCallByBridgeRequest(requestConfig)

    const { endpoint, headers, data } = requestConfig

    const method = requestConfig.method.toLowerCase()

    let bridgeResponse: IWbHttpData

    if (headers && headers.bridge) {
      headers.bridge['x-inter-frontend-session'] = String(MonitoringService.frontendSession)
    }

    const headersWithSession = headers?.bridge ?? undefined

    try {
      switch (method) {
        case 'get': {
          bridgeResponse = await interWbHttp.get(endpoint.bridge, headersWithSession)
          break
        }
        case 'post': {
          bridgeResponse = await interWbHttp.post(endpoint.bridge, data, headersWithSession)
          break
        }
        case 'put': {
          bridgeResponse = await interWbHttp.put(endpoint.bridge, data, headersWithSession)
          break
        }
        case 'delete': {
          bridgeResponse = await interWbHttp.delete(endpoint.bridge, headersWithSession)
          break
        }
        default: {
          throw new Error(`Method ${method} not supported`)
        }
      }
    } catch (error: unknown) {
      let errorObject: unknown = error
      if (typeof errorObject === 'string') {
        try {
          errorObject = JSON.parse(error as string)
        } catch (e: unknown) {
          console.log(e)
        }
      }

      MultiplatformHttpService.handleBackendErrors(errorObject)
      MultiplatformHttpService.handleAcceptableErrorMessages(errorObject)

      throw error
    }

    logCallByBridgeResponse(bridgeResponse)

    let parsedData = bridgeResponse.response as unknown

    if (typeof parsedData === 'string') {
      try {
        parsedData = JSON.parse(parsedData)
      } catch (e: unknown) {
        console.log(e)
        MonitoringService.noticeError(e as Error, {
          errorCodeRef: 'MultiplatformHttpService.callByBridge.parseResponseToJSON',
        })
      }
    }

    return {
      data: parsedData as T,
      status: bridgeResponse.httpStatus,
      headers: bridgeResponse.headers,
    }
  }

  static handleBackendErrors(errorObject: unknown): void {
    if ((errorObject as IWbHttpData).httpStatus) {
      let errorResponse: unknown = (errorObject as IWbHttpData).response

      if (typeof (errorObject as IWbHttpData).response === 'string') {
        try {
          errorResponse = JSON.parse((errorObject as IWbHttpData).response as string)
        } catch (e: unknown) {
          console.log(e)
        }
      }

      throw new ServiceError(
        STATUS_CODE_ACCEPTED.includes((errorObject as IWbHttpData).httpStatus),
        errorResponse,
        JSON.stringify(errorResponse),
        (errorObject as IWbHttpData).httpStatus,
      )
    }
  }

  static handleAcceptableErrorMessages(errorObject: unknown): void {
    if (
      (errorObject as IWbHttpData).response &&
      BRIDGE_MESSAGES_ACCEPTED.find((message) =>
        (errorObject as IWbHttpData).response.includes(message),
      )
    ) {
      const errorResponse: ErrorResponse = {
        errors: [
          {
            code: (errorObject as IWbHttpData).response,
            message: (errorObject as IWbHttpData).response,
          },
        ],
        totalErrors: 1,
      }
      throw new ServiceError(
        true,
        errorResponse,
        JSON.stringify(errorResponse),
        (errorObject as IWbHttpData).httpStatus,
      )
    }
  }

  static async doExecute<T>(config: MultiplatformHttpRequestConfig): Promise<ServiceResponse<T>> {
    if (BridgeService.isBrowser()) {
      const INTER_ENV = EnvVariableService.getVariable(EnvVariableKeys.INTER_ENV)
      if (INTER_ENV === 'production') {
        return Promise.reject(
          new Error('Não é possível executar ambiente de produção via browser.'),
        )
      }

      return MultiplatformHttpService.callByBrowser(config)
    }

    return MultiplatformHttpService.callByBridge(config)
  }
}
